import { TypedDocumentNode, gql } from '@apollo/client';
import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { useLoadSuggestLineup } from 'hooks/so5/useLoadSuggestLineup';
import { hasSomeCardsForLeaderboard } from 'lib/so5';

import { LeagueTrckSlotCta } from '../LeagueTrckSlotCta';
import { LeagueTrckSlots } from '../LeagueTrckSlots';
import { getLeagueTrackSlotsInfo } from '../utils';
import type { LeagueTrckSlotsPicker_so5LeagueTrack } from './__generated__/index.graphql';

const Buttons = styled(Vertical)`
  width: 100%;
  padding: 0 var(--double-unit);
`;

type Props = {
  so5LeagueTrack: LeagueTrckSlotsPicker_so5LeagueTrack;
  focusOnFirstEmptySlot?: boolean;
  showSuggest?: boolean;
};

export const LeagueTrckSlotsPicker = ({
  so5LeagueTrack,
  focusOnFirstEmptySlot,
  showSuggest,
}: Props) => {
  const {
    flags: { useSuggestLineup = false },
  } = useFeatureFlags();

  const [loadSuggestLineup, { loading: loadingLoadSuggestLineup }] =
    useLoadSuggestLineup();

  const { firstNotComposedSlot, nbContenders: firstEmptySlot } =
    getLeagueTrackSlotsInfo(so5LeagueTrack);

  const [selectedIndex, setSelectedIndex] = useState<number>(
    focusOnFirstEmptySlot ? firstEmptySlot : firstNotComposedSlot
  );

  const selectedContender =
    so5LeagueTrack.mySo5LeaderboardContenders.at(selectedIndex);

  const showSuggestButton =
    useSuggestLineup &&
    showSuggest &&
    so5LeagueTrack.entrySo5Leaderboard.eligibleToSuggestedLineupLoader;

  const suggestButtonDisabled =
    !!selectedContender?.so5Lineup ||
    !hasSomeCardsForLeaderboard(so5LeagueTrack.entrySo5Leaderboard);

  return (
    <>
      <LeagueTrckSlots
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        so5LeagueTrack={so5LeagueTrack}
      />
      <Buttons>
        <LeagueTrckSlotCta
          selectedIndex={selectedIndex}
          so5LeagueTrack={so5LeagueTrack}
        />
        {showSuggestButton && (
          <LoadingButton
            loading={loadingLoadSuggestLineup}
            dots
            disabled={suggestButtonDisabled}
            size="large"
            color="transparent"
            startIcon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            onClick={() => {
              loadSuggestLineup({
                variables: {
                  input: {
                    so5LeaderboardId: so5LeagueTrack.entrySo5Leaderboard.id,
                    managerTeamId:
                      selectedContender?.managerTeam?.id || undefined,
                    shouldCreateManagerTeam:
                      !selectedContender &&
                      so5LeagueTrack.entrySo5Leaderboard.requiresManagerTeam,
                  },
                },
              });
            }}
          >
            <FormattedMessage {...fantasy.suggestLineup} />
          </LoadingButton>
        )}
      </Buttons>
    </>
  );
};

LeagueTrckSlotsPicker.fragments = {
  so5LeagueTrack: gql`
    fragment LeagueTrckSlotsPicker_so5LeagueTrack on So5LeagueTrack {
      slug
      entrySo5Leaderboard {
        slug
        id
        eligibleToSuggestedLineupLoader
        requiresManagerTeam
        ...hasSomeCardsForLeaderboard_so5Leaderboard
      }
      mySo5LeaderboardContenders(includeHiddenManagerTeam: true) {
        slug
        so5Lineup {
          id
        }
        managerTeam {
          id
        }
      }
      ...LeagueTrckSlots_so5LeagueTrack
      ...LeagueTrckSlotCta_so5LeagueTrack
    }
    ${LeagueTrckSlots.fragments.so5LeagueTrack}
    ${LeagueTrckSlotCta.fragments.so5LeagueTrack}
    ${getLeagueTrackSlotsInfo.fragments.so5LeagueTrack}
    ${hasSomeCardsForLeaderboard.fragments.so5Leaderboard}
  ` as TypedDocumentNode<LeagueTrckSlotsPicker_so5LeagueTrack>,
};
